import GetAppIcon from '@mui/icons-material/GetApp';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Theme, useTheme } from '@mui/material/styles';
import React, { Dispatch, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';

import { ExportFormat } from '../../../exports';
import { downloadRequested } from '../../../state';
import { Translation } from '../../../types';
import { useDataGridFlexSales } from '../DataGridFlexSalesProvider';
import { DataGridHandler } from '../DataGridHandler';

const transformOrigin = { horizontal: 'right', vertical: 'top' } as const;
const anchorOrigin = { horizontal: 'right', vertical: 'bottom' } as const;

export const exportFile = (
  handler: DataGridHandler<unknown, unknown>,
  dispatch: Dispatch<AnyAction>,
  type: ExportFormat,
  t: Translation,
  theme: Theme
) => {
  if (handler.exportHasDispatchAction(type)) {
    Promise.resolve(handler.export(type, theme));
  }

  dispatch(
    downloadRequested({
      downloadHandler: () =>
        new Promise<void>((res, rej) => {
          setTimeout(async () => {
            try {
              await Promise.resolve(handler.export(type, theme));
              res();
            } catch (e) {
              rej(e);
            }
          });
        }),
      errorMessage: () => t('errors.exports.body', { type }),
    })
  );
};

interface ExportButtonProps {
  disabled?: boolean;
}

const Export = ({ disabled }: ExportButtonProps) => {
  const { handler } = useDataGridFlexSales();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportExcel = useCallback(
    () => exportFile(handler, dispatch, 'XLSX', t, theme),
    [handler, dispatch, t, theme]
  );

  const handleExportPdf = useCallback(
    () => exportFile(handler, dispatch, 'PDF', t, theme),
    [handler, dispatch, t, theme]
  );

  return (
    <>
      <IconButton
        onClick={handleClick}
        aria-controls={open ? 'report-export-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={disabled}
      >
        <GetAppIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id="report-export-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={transformOrigin}
        anchorOrigin={anchorOrigin}
      >
        <MenuItem onClick={handleExportExcel}>
          {t('toolbarExportButton.labels.xlsxFormat')}
        </MenuItem>
        <MenuItem onClick={handleExportPdf}>{t('toolbarExportButton.labels.pdfFormat')}</MenuItem>
      </Menu>
    </>
  );
};

export default Export;
